/**
 * Локальное хранилище на IndexedDB.
 *
 * Local-first (PRD §13): все расчёты идут на устройстве, приложение работает
 * офлайн. Сервер понадобится для синка и разбора чеков, но не для того, чтобы
 * показать главную цифру.
 *
 * Обёртка написана руками, а не взята библиотекой: нужен ровно один сценарий —
 * несколько типизированных хранилищ по ключу id, — и зависимость ради него
 * добавляет больше, чем экономит.
 */

const DB_NAME = 'skoka';
/**
 * Версия локального хранилища.
 *
 * **Поднимается вместе с каждым новым хранилищем в `STORES`.** Браузер создаёт
 * недостающие хранилища только при повышении версии: у того, кто уже открывал
 * приложение, забытое повышение означает отсутствующий стор и отказ при первом
 * же чтении — то есть приложение, которое не открывается вовсе.
 *
 * Страховка от этой забывчивости — в `openDb` ниже, а проверка, которая ловит
 * её до выкладки, — в `apps/web/test/db-version.test.ts`.
 */
const DB_VERSION = 11;

export const STORES = {
  accounts: 'id',
  creditTerms: 'accountId',
  /**
   * Кредиты: карта, ипотека, автокредит, рассрочка — один список.
   *
   * Условия карт (creditTerms) пока остаются рядом: у тех, кто заводил карту
   * до появления этого списка, они единственный источник. Расчёт берёт и то и
   * другое, отдавая предпочтение кредиту.
   */
  loans: 'id',
  incomeStreams: 'id',
  obligations: 'id',
  goals: 'id',
  /** Журнал событий цели: взнос, пропуск, изъятие — §8.3 плана миграции. */
  goalAllocations: 'key',
  /** Принятые планы периодов: снимок сумм на момент согласия человека. */
  acceptedPlans: 'id',
  /**
   * Оперативные раскладки денег: принятые версии ответа «что я могу сейчас».
   *
   * Отдельно от месячных планов, потому что это другая сущность с другим
   * горизонтом: план считается от ожидаемого дохода, раскладка — от денег на
   * счетах. Версии не перезаписываются, а ссылаются на предыдущую.
   */
  allocations: 'id',
  /** Черновик настройки: возврат продолжает с последнего блока. */
  onboardingDrafts: 'key',
  /**
   * Загруженные электронные чеки — до подтверждения и после (§20).
   *
   * Отдельное хранилище, а не операции: пока человек не подтвердил импорт,
   * денег в нём нет и баланс он не двигает. После подтверждения запись
   * остаётся здесь ссылкой на операцию — по ней и ловится повторная загрузка
   * того же файла.
   */
  receiptImports: 'id',
  plannedExpenses: 'id',
  /**
   * Разовые приходы, которых ждут: вернут долг, продали вещь, премия.
   *
   * Отдельно от доходов, потому что это не поток: одна дата, одна сумма и
   * никакого следующего раза. И отдельно от операций — денег ещё нет.
   */
  expectedIncomes: 'id',
  /**
   * Назначения будущих денег: «эти 70 000 из зарплаты 5-го — на карту».
   *
   * Отдельно от конвертов намеренно. Конверт закрепляет деньги, которые есть;
   * назначение распоряжается деньгами, которых ещё нет. Сложить их в один
   * список значило бы показать человеку обеспеченным то, что держится на
   * обещании работодателя.
   */
  plannedAllocations: 'id',
  /**
   * Согласия: что подтверждено, какой версии документа и когда.
   *
   * Событиями, а не флагом: отзыв добавляет запись, а не стирает прошлую.
   */
  consents: 'id',
  /**
   * Журнал показов наблюдений: что человек уже видел и что отложил.
   *
   * Ключ — устойчивый ключ повода, а не запись: отметка «не сейчас» относится
   * к поводу, и при изменении его сути наблюдение возвращается само.
   */
  insightLog: 'key',
  /**
   * Разговоры со SKOKA: разборы, вопросы и ответы.
   *
   * Лежит на устройстве и в снимок не уезжает. Причина не в размере, а в
   * содержании: разбор привязан к редакции данных того устройства, где он
   * сделан, и на другом телефоне он всё равно был бы помечен устаревшим.
   * Хранится ограниченный хвост — не архив, а память разговора.
   */
  advisorTalk: 'key',
  /**
   * Планы стабильного бюджета: «плачу себе сам, раз в две недели, 30 000 ₽».
   *
   * Отдельно от источников дохода намеренно: выплата себе доходом не
   * является. Положи её в `incomeStreams` — и приложение начнёт считать
   * собственные деньги человека его заработком, обучать по ним ритм и
   * показывать удвоенный месяц.
   */
  stablePlans: 'id',
  /**
   * Состоявшиеся выплаты себе. Ключ — план и дата по расписанию.
   *
   * Устойчивый ключ, а не случайный идентификатор: то же нажатие приходит
   * дважды из двух вкладок, и вторая выплата на ту же дату — это не вторая
   * выплата.
   */
  stablePayouts: 'id',
  /**
   * Движения буфера: пополнения и изъятия.
   *
   * История, а не остаток: сумма буфера живёт в резерве. Без истории месячный
   * отчёт не смог бы показать, проеден запас или пополнен, — а по остатку на
   * конец месяца это неотличимо.
   */
  stableMoves: 'id',
  transactions: 'id',
  transactionItems: 'id',
  reserves: 'id',
  categories: 'id',
  /**
   * Снимки чеков: ключ — идентификатор чека, а не операции.
   *
   * Лежат только на устройстве и в снимок синка не попадают. Причина
   * арифметическая: один чек весит сотни килобайт, а снимок состояния уходит
   * на сервер целиком при каждом изменении. Возить фотографии в каждом
   * сохранении значило бы сделать синхронизацию непригодной ради архива.
   */
  receiptPhotos: 'id',
  meta: 'key',
} as const;

export type StoreName = keyof typeof STORES;

let dbPromise: Promise<IDBDatabase> | null = null;

/**
 * Открыть базу нужной версии.
 *
 * Обработчик обновления создаёт **все** недостающие хранилища из `STORES`, а
 * не только новое: так база, отставшая на несколько версий, догоняет за один
 * шаг.
 */
function openAt(version: number): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, version);
    req.onupgradeneeded = () => {
      const db = req.result;
      for (const [name, keyPath] of Object.entries(STORES)) {
        if (!db.objectStoreNames.contains(name)) {
          db.createObjectStore(name, { keyPath });
        }
      }
    };
    req.onsuccess = () => {
      const db = req.result;
      // Другая вкладка обновит версию — закрываем соединение, чтобы не
      // блокировать её и не работать со устаревшей схемой.
      db.onversionchange = () => db.close();
      resolve(db);
    };
    req.onerror = () => reject(req.error ?? new Error('IndexedDB недоступен'));
    // Без этого обработчика открытие просто зависает навсегда, и любая
    // операция тихо не завершается — самый неприятный вид отказа.
    req.onblocked = () => reject(new Error(
      'Обновление хранилища заблокировано: приложение открыто в другой вкладке. Закрой её и обнови страницу.',
    ));
  });
}

/**
 * Хранилище приложения.
 *
 * Если версию подняли не забыв — открывается сразу. Если забыли, база
 * открывается со старой схемой, и недостающих хранилищ в ней нет: любое
 * чтение отказом, приложение не запускается. Поэтому после открытия состав
 * сверяется со `STORES`, и при нехватке база переоткрывается на версию выше —
 * обработчик обновления доводит схему.
 *
 * Это страховка, а не замена дисциплине: `DB_VERSION` всё равно положено
 * поднимать, и проверка это требует. Но цена забытого числа не должна быть
 * «человек не может открыть свои деньги».
 */
export function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = openAt(DB_VERSION).then((db) => {
    const missing = Object.keys(STORES).filter((n) => !db.objectStoreNames.contains(n));
    if (missing.length === 0) return db;
    console.warn('[db] версия не поднята, хранилища отсутствуют:', missing.join(', '));
    const next = db.version + 1;
    db.close();
    return openAt(next);
  });
  return dbPromise;
}

function tx(db: IDBDatabase, stores: StoreName[], mode: IDBTransactionMode) {
  return db.transaction(stores, mode);
}

const wrap = <T>(req: IDBRequest<T>): Promise<T> =>
  new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });

/*
  Когда данные этого устройства последний раз менялись (`localChangedAt`).

  Нужно листу «Данные расходятся»: без него не сказать, где картина новее —
  на телефоне или на сервере. Отмечается только запись того, что уходит на
  сервер: черновик настройки, фото чеков, разговор и служебные отметки
  синхронизации картину не меняют. Загрузка снимка с сервера ставит время
  самого снимка (`replaceLocalState`), а не «сейчас».
*/
const SYNCED_STORES = new Set<string>([
  'accounts', 'creditTerms', 'loans', 'incomeStreams', 'obligations', 'goals', 'goalAllocations',
  'plannedExpenses', 'plannedAllocations', 'expectedIncomes', 'reserves', 'stablePlans', 'stablePayouts',
  'stableMoves', 'transactions', 'receiptImports', 'categories', 'acceptedPlans', 'allocations', 'consents',
  'insightLog',
]);
const SYNCED_META = new Set<string>([
  'trackingStartedAt', 'obligationsKnown', 'categoryDefaultsReady', 'periodPins', 'periodBaselines', 'dismissedHints',
  'needBaseline', 'wantBaseline', 'profileName', 'focusOverride', 'planMode', 'tradeoffChoice', 'newCategoryMode', 'periodChoice',
  'periodDeals', 'periodTopicsOff', 'periodReviewLater', 'usualPinned', 'accountGroups',
]);
export const LOCAL_CHANGED = 'localChangedAt';
let quiet = 0;
/** Запись без отметки «изменено здесь» — для загрузки снимка с сервера. */
export async function quietly<T>(work: () => Promise<T>): Promise<T> {
  quiet += 1;
  try { return await work(); } finally { quiet -= 1; }
}
const changes = (store: string, value?: unknown): boolean => (store === 'meta'
  ? SYNCED_META.has((value as { key?: string } | undefined)?.key ?? '')
  : SYNCED_STORES.has(store));
async function touch(writes: { store: string; value?: unknown }[]): Promise<void> {
  if (quiet > 0 || !writes.some((w) => changes(w.store, w.value))) return;
  const db = await openDb();
  const t = tx(db, ['meta'], 'readwrite');
  t.objectStore('meta').put({ key: LOCAL_CHANGED, value: new Date().toISOString() });
  await done(t);
}

export async function getAll<T>(store: StoreName): Promise<T[]> {
  const db = await openDb();
  return wrap(tx(db, [store], 'readonly').objectStore(store).getAll() as IDBRequest<T[]>);
}

export async function put<T>(store: StoreName, value: T): Promise<void> {
  const db = await openDb();
  const t = tx(db, [store], 'readwrite');
  t.objectStore(store).put(value);
  await done(t);
  await touch([{ store, value }]);
}

/** Запись пачкой одной транзакцией: либо всё, либо ничего. */
export async function putAll<T>(store: StoreName, values: T[]): Promise<void> {
  if (values.length === 0) return;
  const db = await openDb();
  const t = tx(db, [store], 'readwrite');
  const os = t.objectStore(store);
  for (const v of values) os.put(v);
  await done(t);
  await touch([{ store, value: values[0] }]);
}

/**
 * Прочитать, изменить, записать — в одной транзакции.
 *
 * IndexedDB держит блокировку хранилища на всю транзакцию, поэтому чтение и
 * запись внутри неё не разъезжаются с чужими. Два подтверждения одного платежа
 * из разных вкладок иначе оба читают старый факт и один из них теряется:
 * очередь в памяти одной вкладки о второй вкладке ничего не знает.
 */
export async function mutate<T>(
  store: StoreName, key: IDBValidKey, change: (current: T | undefined) => T | undefined,
): Promise<T | undefined> {
  const db = await openDb();
  const t = tx(db, [store], 'readwrite');
  const os = t.objectStore(store);
  const current = await wrap(os.get(key) as IDBRequest<T | undefined>);
  const next = change(current);
  if (next !== undefined) os.put(next);
  await done(t);
  if (next !== undefined) await touch([{ store, value: next }]);
  return next;
}

/**
 * Полная замена содержимого хранилищ — одной транзакцией.
 *
 * Очистка и запись порознь оставляют окно, в котором данных нет вовсе: сбой
 * записи между ними уничтожает картину человека, а не заменяет её. Здесь либо
 * применяется всё, либо не меняется ничего.
 */
export async function replaceAll(
  parts: { store: StoreName; values: unknown[] }[],
): Promise<void> {
  if (parts.length === 0) return;
  const db = await openDb();
  const t = tx(db, parts.map((p) => p.store), 'readwrite');
  for (const part of parts) {
    const os = t.objectStore(part.store);
    os.clear();
    for (const v of part.values) os.put(v);
  }
  await done(t);
  await touch(parts.map((p) => ({ store: p.store })));
}

export async function remove(store: StoreName, key: IDBValidKey): Promise<void> {
  const db = await openDb();
  const t = tx(db, [store], 'readwrite');
  t.objectStore(store).delete(key);
  await done(t);
  await touch([{ store, value: store === 'meta' ? { key } : undefined }]);
}

export async function getMeta<T>(key: string): Promise<T | undefined> {
  const db = await openDb();
  const row = await wrap(
    tx(db, ['meta'], 'readonly').objectStore('meta').get(key) as IDBRequest<{ key: string; value: T } | undefined>,
  );
  return row?.value;
}

export async function setMeta<T>(key: string, value: T): Promise<void> {
  await put('meta', { key, value });
}

/**
 * Очередь на ключ: два одинаковых действия идут одно за другим.
 *
 * Транзакция даёт атомарность — «либо всё, либо ничего», — но одновременные
 * вызовы этим не разводит: браузер выстраивает пересекающиеся транзакции в
 * очередь, а вот в тестовой реализации IndexedDB они спокойно чередуются.
 * Опираться на доброту среды нельзя: от того, увидит ли второе нажатие
 * запись первого, зависит, уедет ли человеку второй перевод.
 *
 * Поэтому одинаковые по смыслу действия — одна выплата, один пропуск —
 * проходят через общий ключ, и второе ждёт первое. Внутри вкладки это
 * гарантия; между вкладками её даёт очередь транзакций самого браузера, а
 * устойчивый ключ наступления — окончательная проверка в обоих случаях.
 */
const queue = new Map<string, Promise<unknown>>();

export function queued<T>(key: string, work: () => Promise<T>): Promise<T> {
  const previous = queue.get(key) ?? Promise.resolve();
  // Ошибка предыдущего не должна отменять следующий: очередь — про порядок.
  const next = previous.catch(() => undefined).then(work);
  queue.set(key, next.catch(() => undefined));
  return next;
}

/**
 * Доступ к хранилищам внутри одной транзакции.
 *
 * Нарочно узкий: внутри транзакции нельзя ходить в сеть, ждать таймер или
 * дёргать другие обёртки этого модуля — IndexedDB завершит её на первом же
 * пустом обороте очереди, и половина записей уедет отдельно от второй.
 */
export interface TxScope {
  get<T>(store: StoreName, key: IDBValidKey): Promise<T | undefined>;
  all<T>(store: StoreName): Promise<T[]>;
  put<T>(store: StoreName, value: T): void;
  del(store: StoreName, key: IDBValidKey): void;
}

/**
 * Несколько хранилищ — одной транзакцией: либо всё, либо ничего.
 *
 * Понадобилось там, где одно событие меняет сразу несколько картин. Выплата
 * себе трогает резерв, остатки двух счетов, операцию перевода, факт выплаты
 * и журнал буфера. Записанные по одной, эти пять правок дают два способа
 * соврать: при сбое посередине резерв уже уменьшен, а выплаты нет; при двух
 * одновременных нажатиях обе проверки «не выплачено ли уже» проходят по
 * старым данным, и человек переводит себе деньги дважды.
 *
 * **Записи копятся и уходят одним броском в конце.** Это не оптимизация, а
 * единственный способ дать честное «либо всё, либо ничего»: `abort()` после
 * случившейся ошибки не спасает — к тому времени транзакция может уже
 * закрыться сама, как только опустела очередь запросов. А отложенная запись
 * не зависит ни от чьей доброты: до конца работы в хранилище не ушло
 * ничего, и отменять нечего.
 *
 * Цена одна, и её надо помнить: **внутри работы чтение не видит своих же
 * записей**. Для расчётов это естественно — сначала читаем картину, потом
 * решаем, потом пишем.
 *
 * Идемпотентность даёт не транзакция, а ключ: проверка «не сделано ли уже»
 * и запись живут под одной блокировкой хранилищ, и в браузере вторая такая
 * транзакция ждёт первую и видит её результат.
 */
export async function transact<T>(
  stores: StoreName[], work: (scope: TxScope) => Promise<T>,
): Promise<T> {
  const db = await openDb();
  const t = tx(db, stores, 'readwrite');
  /*
    Подписка на завершение — **до** работы, а не после.

    Транзакция завершается сама, как только очередь запросов опустела. Если
    подписаться после `work`, короткая работа успеет её закрыть, и события
    уже не будет: `await` повиснет навсегда, а операция снаружи выглядит
    незавершённой.
  */
  const finished = done(t);
  const writes: (() => void)[] = [];
  const touched: { store: string; value?: unknown }[] = [];
  const scope: TxScope = {
    get: <V>(store: StoreName, key: IDBValidKey) =>
      wrap(t.objectStore(store).get(key) as IDBRequest<V | undefined>),
    all: <V>(store: StoreName) => wrap(t.objectStore(store).getAll() as IDBRequest<V[]>),
    put: (store, value) => { touched.push({ store, value }); writes.push(() => { t.objectStore(store).put(value); }); },
    del: (store, key) => { touched.push({ store, value: store === 'meta' ? { key } : undefined }); writes.push(() => { t.objectStore(store).delete(key); }); },
  };

  let result: T;
  try {
    result = await work(scope);
    // Один бросок, без единого `await` между записями: разорвать его нечем.
    for (const write of writes) write();
  } catch (e) {
    try { t.abort(); } catch { /* уже завершена — записей всё равно не было */ }
    await finished.catch(() => undefined);
    throw e;
  }
  await finished;
  await touch(touched);
  return result;
}

/** Полная очистка — «начать с чистого листа» и «сбросить всё» (SCREENS §11.4). */
export async function clearStores(stores: StoreName[]): Promise<void> {
  const db = await openDb();
  const t = tx(db, stores, 'readwrite');
  for (const s of stores) t.objectStore(s).clear();
  await done(t);
}

const done = (t: IDBTransaction): Promise<void> =>
  new Promise((resolve, reject) => {
    t.oncomplete = () => resolve();
    t.onerror = () => reject(t.error);
    t.onabort = () => reject(t.error ?? new Error('Транзакция прервана'));
  });
